import _extends from "@babel/runtime/helpers/esm/extends";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/* eslint-disable no-debugger */

/* eslint-disable no-console */
import React, { useMemo, useRef, useEffect, useState } from "react";
import { useTable } from "react-table";
import { DATA_GRID_DISPLAY_NAME, defaultPropertiesOverride, ITEMS_PER_PAGE_AND_VISIBLE_PAGES_MAP, ERROR_MSG } from "./constants";
import { TableBody, TableHeader } from "./components";
import { applySortingPlugin, applyPaginationPlugin, addMultiRowSelection, applySelectionPlugin, addSingleRowSelection } from "./plugins";
import { getClassName } from "./utils";
import { TableFilter } from "./components/TableFilter";
export var DataGrid = function DataGrid(props) {
  var _selection$renderTogg;

  var ariaDescribedBy = props.ariaDescribedBy,
      ariaLabelledBy = props.ariaLabelledBy,
      data = props.data,
      columns = props.columns,
      _props$initialState = props.initialState,
      initialState = _props$initialState === void 0 ? {} : _props$initialState,
      sorting = props.sorting,
      selection = props.selection,
      caption = props.caption,
      pagination = props.pagination,
      className = props.className,
      useDefaultColspan = props.useDefaultColspan,
      getRowId = props.getRowId,
      getHeaderProps = props.getHeaderProps,
      getCellProps = props.getCellProps,
      getColumnProps = props.getColumnProps,
      getRowProps = props.getRowProps,
      renderPagination = props.renderPagination,
      renderFilters = props.renderFilters,
      enableShowMore = props.enableShowMore,
      maxTextLength = props.maxTextLength,
      clearFilterFlag = props.clearFilterFlag;
  var tableRef = useRef();
  var wasFirstRenderRef = useRef(false);
  var prevSelectedRowIds = useRef(initialState.selectedRowIds);
  var itemsPerPageAndVisiblePagesMap = (pagination === null || pagination === void 0 ? void 0 : pagination.itemsPerPageValues) || ITEMS_PER_PAGE_AND_VISIBLE_PAGES_MAP;
  var itemsPerPageValues = Object.keys(itemsPerPageAndVisiblePagesMap).map(function (item) {
    return Number(item);
  });

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      tableData = _useState2[0],
      setTableData = _useState2[1];

  useEffect(function () {
    setTableData(data);
  }, [data]);

  var _useMemo = useMemo(function () {
    var config = {
      columns: columns,
      data: tableData,
      initialState: initialState,
      getRowId: getRowId
    };
    var extensions = [];

    if (sorting) {
      applySortingPlugin({
        config: config,
        extensions: extensions,
        sorting: sorting
      });
    }

    if (pagination) {
      applyPaginationPlugin({
        config: config,
        extensions: extensions,
        pagination: pagination
      });
    }

    if (selection) {
      applySelectionPlugin({
        config: config,
        extensions: extensions,
        selection: selection
      });
    }

    return [config, extensions];
  }, [tableData, columns, data]),
      _useMemo2 = _slicedToArray(_useMemo, 2),
      tableConfig = _useMemo2[0],
      tableExtensions = _useMemo2[1];

  var _useTable = useTable.apply(void 0, [tableConfig].concat(_toConsumableArray(tableExtensions), [function (hooks) {
    if ((selection === null || selection === void 0 ? void 0 : selection.isMulti) === true) {
      addMultiRowSelection(hooks.visibleColumns, getClassName);
    } else if ((selection === null || selection === void 0 ? void 0 : selection.isMulti) === false) {
      addSingleRowSelection(hooks.visibleColumns, getClassName, tableRef);
    }
  }])),
      page = _useTable.page,
      rows = _useTable.rows,
      headerGroups = _useTable.headerGroups,
      _useTable$state = _useTable.state,
      pageIndex = _useTable$state.pageIndex,
      pageSize = _useTable$state.pageSize,
      sortBy = _useTable$state.sortBy,
      selectedRowIds = _useTable$state.selectedRowIds,
      selectedFlatRows = _useTable.selectedFlatRows,
      pageCount = _useTable.pageCount,
      nextPage = _useTable.nextPage,
      previousPage = _useTable.previousPage,
      isAllRowsSelected = _useTable.isAllRowsSelected,
      canPreviousPage = _useTable.canPreviousPage,
      canNextPage = _useTable.canNextPage,
      setPageSize = _useTable.setPageSize,
      gotoPage = _useTable.gotoPage,
      getTableProps = _useTable.getTableProps,
      getTableBodyProps = _useTable.getTableBodyProps,
      getToggleAllPageRowsSelectedProps = _useTable.getToggleAllPageRowsSelectedProps,
      getToggleAllRowsSelectedProps = _useTable.getToggleAllRowsSelectedProps,
      prepareRow = _useTable.prepareRow,
      toggleRowSelected = _useTable.toggleRowSelected,
      toggleAllRowsSelected = _useTable.toggleAllRowsSelected,
      toggleAllPageRowsSelected = _useTable.toggleAllPageRowsSelected;

  var visibleRows = pagination || renderPagination ? page : rows;

  var _useState3 = useState(rows.length),
      _useState4 = _slicedToArray(_useState3, 2),
      totalPages = _useState4[0],
      setTotalPages = _useState4[1];

  useEffect(function () {
    setTotalPages(rows.length);
  }, [rows]);
  /**
   * This hook is designed to reflect changes in selected rows and
   * call the specific callback - onRowSelection
   */

  useEffect(function () {
    if (selection !== null && selection !== void 0 && selection.onRowSelection) {
      /**
       * Optimization to avoid calling onRowSelection again
       */
      var prevSelectedRowIdsArray = Object.keys(prevSelectedRowIds.current);
      var selectedRowIdsArray = Object.keys(selectedRowIds);
      var isSelectedValuesSame = prevSelectedRowIdsArray.length === selectedRowIdsArray.length && prevSelectedRowIdsArray.every(function (item, index) {
        return item === selectedRowIdsArray[index];
      });

      if (!isSelectedValuesSame) {
        var selectedRows = selectedFlatRows.map(function (_ref) {
          var original = _ref.original;
          return original;
        });
        selection.onRowSelection({
          selectedRows: selectedRows,
          selectedRowIds: selectedRowIds
        });
        prevSelectedRowIds.current = selectedRowIds;
      }
    }
  }, [selectedFlatRows, selectedRowIds]);
  /**
   * This hook is designed to avoid setting the page on first render
   * as it can be configured by default
   */

  useEffect(function () {
    if (wasFirstRenderRef.current) {
      if (gotoPage) {
        gotoPage(0);
      }

      if (sorting !== null && sorting !== void 0 && sorting.onSortChange) {
        sorting.onSortChange(sortBy);
      }
    }

    wasFirstRenderRef.current = true;
  }, [sortBy]);

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      showFilterModal = _useState6[0],
      setShowFilterModal = _useState6[1];

  var _useState7 = useState({
    left: 0
  }),
      _useState8 = _slicedToArray(_useState7, 2),
      modalPosition = _useState8[0],
      setModalPosition = _useState8[1];

  var _useState9 = useState({
    data: [],
    colIndex: -1
  }),
      _useState10 = _slicedToArray(_useState9, 2),
      checkOptions = _useState10[0],
      setCheckOptions = _useState10[1];

  var _useState11 = useState({
    data: [],
    colIndex: -1
  }),
      _useState12 = _slicedToArray(_useState11, 2),
      checkOptionsFilter = _useState12[0],
      setCheckOptionsFilter = _useState12[1];

  var _useState13 = useState(-1),
      _useState14 = _slicedToArray(_useState13, 2),
      currentIndex = _useState14[0],
      setCurrentIndex = _useState14[1];

  var _useState15 = useState(false),
      _useState16 = _slicedToArray(_useState15, 2),
      selectAllChecked = _useState16[0],
      setSelectAllChecked = _useState16[1];

  var _useState17 = useState({
    isError: false,
    message: ""
  }),
      _useState18 = _slicedToArray(_useState17, 2),
      filterErrorState = _useState18[0],
      setFilterErrorState = _useState18[1];

  var divRef = useRef(null);
  useEffect(function () {
    var handleKeyDown = function handleKeyDown(event) {
      if (event.key === "Escape") {
        setShowFilterModal(false);
      }
    };

    var handleClickOutside = function handleClickOutside(event) {
      if (divRef.current && !divRef.current.contains(event.target)) {
        setShowFilterModal(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return function () {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []); // Empty dependency array means this effect runs once on mount and cleanup on unmount

  var checkSelectAllHandler = function checkSelectAllHandler(checkArray) {
    if (checkArray.every(function (item) {
      return item.isSelected === true;
    })) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
  };

  var setShowFilterModalFun = function setShowFilterModalFun(event, index) {
    var currentTargetElement = event.currentTarget;

    if (currentTargetElement && currentTargetElement.closest) {
      var headerCellClass = getClassName({
        descendantName: "cell"
      });
      var headerCell = currentTargetElement.closest(".".concat(headerCellClass));

      if (headerCell) {
        var rect = headerCell.getBoundingClientRect();
        var columnIndex = headerCell.parentElement ? Array.from(headerCell.parentElement.children).indexOf(headerCell) : -1;

        if (columnIndex === 0) {
          setModalPosition(_objectSpread(_objectSpread({}, modalPosition), {}, {
            left: 0
          }));
        } else {
          setModalPosition(_objectSpread(_objectSpread({}, modalPosition), {}, {
            left: rect.left - 150
          }));
        }
      }
    }

    setShowFilterModal(!showFilterModal);

    function getDistinctValues(array, key) {
      var result = array.map(function (item) {
        if (item.hasOwnProperty("original".concat(key))) {
          var originalValue = item["original".concat(key)];
          return item["".concat(key, "Type")] === "object" ? originalValue.props.children : originalValue;
        }

        return item[key];
      }) // Extract values for the specified key
      .filter(function (value, distIndex, self) {
        return self.indexOf(value) === distIndex;
      }); // Filter for distinct values

      function convertTableValue(value) {
        var dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
        var match = value.match(dateRegex);

        if (match) {
          var _match$map = match.map(Number),
              _match$map2 = _slicedToArray(_match$map, 4),
              month = _match$map2[1],
              day = _match$map2[2],
              year = _match$map2[3];

          return "".concat(month.toString().padStart(2, "0"), "/").concat(day.toString().padStart(2, "0"), "/").concat(year);
        }

        if (/^\d/.test(value)) {
          return " ".concat(value);
        }

        return value;
      }

      var convertedResult = result.map(function (item) {
        return convertTableValue(item);
      });
      var sortedOptions = convertedResult.sort(function (a, b) {
        return String(a).localeCompare(String(b), "en", {
          numeric: true,
          sensitivity: "base"
        });
      });
      return sortedOptions;
    }

    var distinctCheckOptions = getDistinctValues(data, "col".concat(index)).map(function (option, checkIndex) {
      return {
        id: "check-".concat(checkIndex),
        title: option,
        isSelected: false
      };
    });

    if (checkOptionsFilter.colIndex === index) {
      checkSelectAllHandler(checkOptionsFilter.data);
      setCheckOptions({
        data: checkOptionsFilter.data,
        colIndex: index
      });
      setCurrentIndex(index);
    } else {
      setCheckOptions({
        data: distinctCheckOptions,
        colIndex: index
      });
      setCurrentIndex(index);
    }
  };

  var filterModalhandler = function filterModalhandler() {
    setSelectAllChecked(false);
    setFilterErrorState({
      isError: false,
      message: ""
    });
    setShowFilterModal(!showFilterModal);
  };

  var checkOptionsHanlder = function checkOptionsHanlder(checkValue) {
    var updatedCheckOptions = checkOptions.data.map(function (option) {
      return _objectSpread(_objectSpread({}, option), {}, {
        isSelected: option.title.toString() === checkValue.target.value ? !option.isSelected : option.isSelected
      });
    });
    setCheckOptions(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        data: updatedCheckOptions
      });
    });
    checkSelectAllHandler(updatedCheckOptions);
  };

  var handleSelectAll = function handleSelectAll() {
    if (selectAllChecked) {
      var selectCheckOptions = checkOptions.data.map(function (option) {
        return _objectSpread(_objectSpread({}, option), {}, {
          isSelected: false
        });
      });
      setCheckOptions(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), {}, {
          data: selectCheckOptions
        });
      });
      setSelectAllChecked(false);
    } else {
      var clearCheckOptions = checkOptions.data.map(function (option) {
        return _objectSpread(_objectSpread({}, option), {}, {
          isSelected: true
        });
      });
      setCheckOptions(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), {}, {
          data: clearCheckOptions
        });
      });
      setSelectAllChecked(true);
    }
  };

  var getFilterData = function getFilterData() {
    var checkedData = checkOptions.data.filter(function (item) {
      return item.isSelected;
    }).map(function (item) {
      return item.title.trim();
    });
    var filterDataByCols = data.filter(function (item) {
      var currentCol = item.hasOwnProperty("originalcol".concat(currentIndex)) ? "originalcol".concat(currentIndex) : "col".concat(currentIndex);

      if (_typeof(item[currentCol]) === "object") {
        return checkedData.includes(item[currentCol].props.children);
      }

      return checkedData.includes(item[currentCol]);
    });

    if (filterDataByCols.length) {
      setTableData(filterDataByCols);
      setCheckOptionsFilter(checkOptions);
      filterModalhandler();
      setTotalPages(filterDataByCols.length);
    } else {
      setFilterErrorState({
        isError: true,
        message: ERROR_MSG
      });
    }
  };

  var clearFilterData = function clearFilterData() {
    setTableData(data);
    var clearCheckOptions = checkOptions.data.map(function (option) {
      return _objectSpread(_objectSpread({}, option), {}, {
        isSelected: false
      });
    });
    setCheckOptions(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        data: clearCheckOptions
      });
    });
    setCheckOptionsFilter({
      data: [],
      colIndex: -1
    });
    setSelectAllChecked(false);
  };

  useEffect(function () {
    if (clearFilterFlag) {
      clearFilterData();
    }
  }, [data]);
  return /*#__PURE__*/React.createElement("div", {
    className: getClassName({
      descendantName: "wrapper",
      className: className
    })
  }, selection === null || selection === void 0 ? void 0 : (_selection$renderTogg = selection.renderToggleSelection) === null || _selection$renderTogg === void 0 ? void 0 : _selection$renderTogg.call(selection, {
    selectedFlatRows: selectedFlatRows,
    selectedRowIds: selectedRowIds,
    isAllRowsSelected: isAllRowsSelected,
    getToggleAllPageRowsSelectedProps: getToggleAllPageRowsSelectedProps,
    getToggleAllRowsSelectedProps: getToggleAllRowsSelectedProps,
    toggleRowSelected: toggleRowSelected,
    toggleAllRowsSelected: toggleAllRowsSelected,
    toggleAllPageRowsSelected: toggleAllPageRowsSelected
  }), /*#__PURE__*/React.createElement("div", {
    className: getClassName({
      descendantName: "tableWrapper"
    })
  }, /*#__PURE__*/React.createElement("table", _extends({}, getTableProps(), defaultPropertiesOverride, {
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    className: getClassName({
      descendantName: "table"
    })
  }), caption && /*#__PURE__*/React.createElement("caption", {
    className: getClassName({
      descendantName: "caption"
    })
  }, caption), /*#__PURE__*/React.createElement("thead", {
    className: getClassName({
      descendantName: "tableHeader"
    })
  }, /*#__PURE__*/React.createElement(TableHeader, {
    headerGroups: headerGroups,
    sorting: sorting,
    useDefaultColspan: useDefaultColspan,
    getColumnProps: getColumnProps,
    getHeaderProps: getHeaderProps,
    setShowFilterModalFun: setShowFilterModalFun
  })), renderFilters && renderFilters({
    tableRef: tableRef
  }), showFilterModal && /*#__PURE__*/React.createElement("div", {
    ref: divRef,
    className: getClassName({
      descendantName: "modal"
    }),
    style: {
      left: modalPosition.left
    }
  }, /*#__PURE__*/React.createElement(TableFilter, {
    checkOptions: checkOptions.data,
    checkOptionsHanlder: checkOptionsHanlder,
    filterModalhandler: filterModalhandler,
    getFilterData: getFilterData,
    clearFilterData: clearFilterData,
    handleSelectAll: handleSelectAll,
    selectAllChecked: selectAllChecked,
    filterErrorState: filterErrorState
  })), /*#__PURE__*/React.createElement("tbody", _extends({}, getTableBodyProps(), defaultPropertiesOverride, {
    ref: tableRef,
    className: getClassName({
      descendantName: "tableBody"
    })
  }), /*#__PURE__*/React.createElement(TableBody, {
    rows: visibleRows,
    columns: columns,
    prepareRow: prepareRow,
    getCellProps: getCellProps,
    getColumnProps: getColumnProps,
    getRowProps: getRowProps,
    enableShowMore: enableShowMore,
    maxTextLength: maxTextLength
  })))), pagination && (renderPagination === null || renderPagination === void 0 ? void 0 : renderPagination({
    pageSize: pageSize,
    canPreviousPage: canPreviousPage,
    canNextPage: canNextPage,
    gotoPage: gotoPage,
    previousPage: previousPage,
    totalItemsCount: totalPages,
    nextPage: nextPage,
    currentPage: pageIndex + 1,
    pageCount: pageCount,
    numberOfVisiblePages: itemsPerPageAndVisiblePagesMap[pageSize],
    rowsPerPageValues: itemsPerPageValues,
    onPageChange: function onPageChange(newPage) {
      return gotoPage(newPage - 1);
    },
    onPageSizeChange: function onPageSizeChange(newPageSize) {
      gotoPage(0);
      setPageSize(newPageSize);
    }
  })));
};
DataGrid.displayName = DATA_GRID_DISPLAY_NAME;